import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';
import { DrawerOpenEvent } from '@theme/theme-drawer';

/**
 * A custom element that manages cart drawer behavior within a `<theme-drawer>`.
 *
 * Dialog lifecycle (open/close, squeeze, history, animations) is owned by `<theme-drawer>`.
 * The `cart:view` event is auto-dispatched by `CartItemsComponent` via the
 * `view-event-trigger="dialog"` attribute (see `snippets/cart-items-component.liquid`).
 * Cart count announcements are owned by `<header-actions>`.
 * This component handles the remaining cart-specific concerns: auto-open on add-to-cart,
 * sticky summary layout, and the installments CTA close-on-click.
 *
 * @extends {Component}
 */
class CartDrawerComponent extends Component {
  /** @type {number} */
  #summaryThreshold = 0.5;

  /** @type {import('@theme/theme-drawer').ThemeDrawer | null} */
  get #themeDrawer() {
    return /** @type {import('@theme/theme-drawer').ThemeDrawer | null} */ (this.closest('theme-drawer'));
  }

  /** @type {HTMLDialogElement | null} */
  get #dialog() {
    return this.closest('dialog');
  }

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartLinesUpdate);
    this.#themeDrawer?.addEventListener(DrawerOpenEvent.eventName, this.#handleDrawerOpen);

    // The restore path sets [open] before this module loads, so the
    // theme-drawer:open event will have already fired. Use the attribute
    // check so this works even before <theme-drawer> upgrades.
    if (this.#themeDrawer?.hasAttribute('open')) {
      this.#handleDrawerOpen();
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#handleCartLinesUpdate);
    this.#themeDrawer?.removeEventListener(DrawerOpenEvent.eventName, this.#handleDrawerOpen);
  }

  /**
   * Handles the theme-drawer opening — updates sticky state and wires up the installments CTA.
   */
  #handleDrawerOpen = () => {
    this.#updateStickyState();

    // Close cart drawer when installments CTA is clicked to avoid overlapping dialogs.
    // Re-queried on every open so it survives cart content re-renders that
    // replace the shopify-payment-terms shadow root.
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const cta = this.querySelector('shopify-payment-terms')?.shadowRoot?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', () => this.#themeDrawer?.close(), { once: true });
    });
  };

  /**
   * @param {import('@shopify/events').CartLinesUpdateEvent} event
   */
  #handleCartLinesUpdate = (event) => {
    const shouldAutoOpen = this.hasAttribute('auto-open') && event.action === 'add' && !this.#themeDrawer?.isOpen;

    // When the event originates inside an open MODAL <dialog> (e.g. quick-add),
    // defer the auto-open until that dialog's native `close` fires so its focus
    // restoration runs first — otherwise we'd capture the wrong
    // `#previouslyFocused`. Non-modal dialogs (e.g. the hotspot preview) don't
    // close on add and don't move focus, so `:modal` excludes them.
    const sourceModal = /** @type {HTMLDialogElement | null} */ (
      event.target instanceof Element ? event.target.closest('dialog:modal') : null
    );

    if (shouldAutoOpen && !sourceModal && !this.#isCartEmpty()) {
      this.#themeDrawer?.open();
    }

    event.promise
      ?.then(({ detail }) => {
        const settle = () => requestAnimationFrame(() => this.#updateStickyState());

        if (!shouldAutoOpen || detail?.didError) {
          settle();
          return;
        }

        const openAndSettle = () => {
          if (!this.#themeDrawer?.isOpen) this.#themeDrawer?.open();
          settle();
        };

        if (sourceModal?.open) {
          sourceModal.addEventListener('close', openAndSettle, { once: true });
        } else {
          openAndSettle();
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[cart-drawer] Event promise rejected:', error);
      });
  };

  #isCartEmpty() {
    return Boolean(this.querySelector('.cart-drawer--empty'));
  }

  #updateStickyState() {
    const dialog = this.#dialog;
    if (!dialog) return;

    // Refs do not cross nested `*-component` boundaries (e.g., `cart-items-component`), so we query within the dialog.
    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      // Ensure the dialog doesn't get stuck in "unsticky" mode when summary disappears (e.g., empty cart).
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;
    dialog.setAttribute('cart-summary-sticky', ratio > this.#summaryThreshold ? 'false' : 'true');
  }

  #attachMoreIndicator() {
    const scroller = this.querySelector('.cart-drawer__content');
    const indicator = this.querySelector('[data-cart-more-indicator]');

    if (!scroller || !indicator) return;

    this.#moreIndicator = { scroller, indicator };
    this.#moreIndicatorObserver = new ResizeObserver(this.#updateMoreIndicator);
    scroller.addEventListener('scroll', this.#updateMoreIndicator);
    indicator.addEventListener('click', this.#handleIndicatorClick);
    this.#moreIndicatorObserver.observe(scroller);
    this.#updateMoreIndicator();
  }

  #detachMoreIndicator() {
    if (!this.#moreIndicator) return;

    this.#moreIndicator.scroller.removeEventListener('scroll', this.#updateMoreIndicator);
    this.#moreIndicator.indicator.removeEventListener('click', this.#handleIndicatorClick);
    this.#moreIndicatorObserver?.disconnect();
    this.#moreIndicatorObserver = null;
    this.#moreIndicator = null;
  }

  #updateMoreIndicator = () => {
    if (!this.#moreIndicator) return;

    const { scroller, indicator } = this.#moreIndicator;
    const rows = scroller.querySelectorAll('.cart-items__table-row');
    if (rows.length <= 1) {
      indicator.setAttribute('hidden', 'hidden');
      return;
    }

    const maxScroll = scroller.scrollHeight - scroller.clientHeight;
    const hide = maxScroll <= 4 || scroller.scrollTop >= maxScroll - 16;
    indicator.toggleAttribute('hidden', hide);
  };

  #handleIndicatorClick = () => {
    if (!this.#moreIndicator) return;
    const { scroller } = this.#moreIndicator;
    const rows = [...scroller.querySelectorAll('.cart-items__table-row')];
    if (!rows.length) return;

    const scrollerRect = scroller.getBoundingClientRect();
    const summary = scroller.querySelector('.cart-drawer__summary');
    const visibleBottom = summary ? summary.getBoundingClientRect().top : scrollerRect.bottom;
    const nextRow = rows.find((row) => row.getBoundingClientRect().bottom > visibleBottom + 4);

    if (nextRow) {
      const rowRect = nextRow.getBoundingClientRect();
      const scrollTarget = rowRect.top - scrollerRect.top + scroller.scrollTop;
      scroller.scrollTo({
        top: scrollTarget,
        behavior: 'smooth',
      });
      return;
    }

    scroller.scrollBy({
      top: scroller.clientHeight * 0.8,
      behavior: 'smooth',
    });
  };

  #moreIndicator = null;
  #moreIndicatorObserver = null;
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
